import React, { useEffect, useState } from 'react';
import { Form, Alert, Spinner } from 'react-bootstrap';
import { downloadFirmRetainerFile } from '../../../../Settings/ClientConnectFirmSettings/ClientWrittenRetainerCard/firmRetainerFuncs';
import PdfDropzone from '../../../components/PdfDropzone';
import { isAgreementProvince } from '../../../../../../../share/utils/agreementProvince';

const VAL_UPLOAD = '__upload__';
const VAL_WRITE = '__write__';

const firmOptionValue = (fileName: string) => `firm:${encodeURIComponent(fileName)}`;

const parseFirmOptionValue = (v: string): string | null => {
    if (!v.startsWith('firm:')) {
        return null;
    }
    return decodeURIComponent(v.slice('firm:'.length));
};

type RetainerSourceMode = '' | 'firm' | 'upload' | 'text';

export type RetainerUploadComponentProps = {
    setRetainerState: (arg0: {
        retainerRequired: any;
        defaultRetainerRequired: any;
        textBoxRetainerRequired: any;
        uploadRetainer: boolean;
        textBasedRetainer: string | null;
    }) => void;
    setUploadedFile: (arg0: File | null) => void;
    retainerState: {
        retainerRequired: boolean;
        textBoxRetainerRequired: boolean;
        uploadRetainer: any;
        defaultRetainerRequired: any;
        textBasedRetainer: any;
    };
    /** Current portal retainer file (upload or firm template), for dropzone display */
    uploadedRetainerFile?: File | null;
    firmRetainerFileNames?: string[];
    firmRetainerSessionId?: string;
    firmRetainerProvince?: string;
    firmRetainerSourceName?: string | null;
    setFirmRetainerSourceName?: (name: string | null) => void;
}

const RetainerUploadComponent = (props: RetainerUploadComponentProps) => {
    const [retainerSourceMode, setRetainerSourceMode] = useState<RetainerSourceMode>('');
    const [selectedFirmFileName, setSelectedFirmFileName] = useState<string | null>(null);
    const [firmDownloading, setFirmDownloading] = useState(false);
    const [firmSelectError, setFirmSelectError] = useState<string | null>(null);

    // MB/SK label this feature "Agreement" (UI only; the retainer feature is unchanged).
    const agreementMode = isAgreementProvince(props.firmRetainerProvince);
    const noun = agreementMode ? 'Agreement' : 'Written Retainer';
    const firmNames = props.firmRetainerFileNames ?? [];
    const firmListKey = (props.firmRetainerFileNames ?? []).join('\u0001');

    useEffect(() => {
        if (!props.retainerState.retainerRequired) {
            setRetainerSourceMode('');
            setSelectedFirmFileName(null);
            setFirmSelectError(null);
        }
    }, [props.retainerState.retainerRequired]);

    /** After navigating away and back (or wizard remount), parent still holds the File + flags — restore firm vs upload in the dropdown. */
    useEffect(() => {
        if (firmDownloading) {
            return;
        }
        if (!props.retainerState.retainerRequired) {
            return;
        }
        if (props.retainerState.textBoxRetainerRequired) {
            setRetainerSourceMode('text');
            setSelectedFirmFileName(null);
            return;
        }
        if (!props.retainerState.uploadRetainer) {
            return;
        }
        const file = props.uploadedRetainerFile;
        if (file && firmNames.length === 0) {
            return;
        }
        if (file && firmNames.length > 0) {
            const src = props.firmRetainerSourceName ?? null;
            if (
                src &&
                src === file.name &&
                firmNames.includes(src)
            ) {
                setRetainerSourceMode('firm');
                setSelectedFirmFileName(src);
            } else {
                setRetainerSourceMode('upload');
                setSelectedFirmFileName(null);
            }
            return;
        }
        if (!file) {
            setRetainerSourceMode('upload');
            setSelectedFirmFileName(null);
        }
    }, [
        firmDownloading,
        firmListKey,
        props.retainerState.retainerRequired,
        props.retainerState.textBoxRetainerRequired,
        props.retainerState.uploadRetainer,
        props.uploadedRetainerFile,
        props.firmRetainerSourceName,
    ]);

    const selectRetainerValue = ((): string => {
        if (!props.retainerState.retainerRequired) {
            return '';
        }
        if (props.retainerState.textBoxRetainerRequired) {
            return VAL_WRITE;
        }
        if (!props.retainerState.uploadRetainer) {
            return '';
        }
        if (retainerSourceMode === 'firm' && selectedFirmFileName) {
            return firmOptionValue(selectedFirmFileName);
        }
        return VAL_UPLOAD;
    })();

    const handleRequireRetainer = (e: React.ChangeEvent<HTMLInputElement>) => {
        const checked = e.target.checked;
        if (!checked) {
            setRetainerSourceMode('');
            setSelectedFirmFileName(null);
            setFirmSelectError(null);
        }
        props.setRetainerState({
            retainerRequired: checked,
            defaultRetainerRequired: false,
            textBoxRetainerRequired: false,
            uploadRetainer: false,
            textBasedRetainer: null
        });
        if (!checked) {
            props.setUploadedFile(null);
            props.setFirmRetainerSourceName?.(null);
        }
    };

    const resetToPlaceholder = () => {
        setRetainerSourceMode('');
        setSelectedFirmFileName(null);
        setFirmSelectError(null);
        props.setFirmRetainerSourceName?.(null);
        props.setUploadedFile(null);
        props.setRetainerState({
            retainerRequired: true,
            defaultRetainerRequired: false,
            textBoxRetainerRequired: false,
            uploadRetainer: false,
            textBasedRetainer: null
        });
    };

    const handleSelectRetainerChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
        const v = e.target.value;
        setFirmSelectError(null);

        if (!v) {
            resetToPlaceholder();
            return;
        }

        if (v === VAL_WRITE) {
            setRetainerSourceMode('text');
            setSelectedFirmFileName(null);
            props.setFirmRetainerSourceName?.(null);
            props.setUploadedFile(null);
            props.setRetainerState({
                retainerRequired: true,
                defaultRetainerRequired: false,
                textBoxRetainerRequired: true,
                uploadRetainer: false,
                textBasedRetainer: props.retainerState.textBasedRetainer ?? null
            });
            return;
        }

        if (v === VAL_UPLOAD) {
            setRetainerSourceMode('upload');
            setSelectedFirmFileName(null);
            props.setFirmRetainerSourceName?.(null);
            props.setUploadedFile(null);
            props.setRetainerState({
                retainerRequired: true,
                defaultRetainerRequired: false,
                textBoxRetainerRequired: false,
                uploadRetainer: true,
                textBasedRetainer: null
            });
            return;
        }

        const fname = parseFirmOptionValue(v);
        if (!fname) {
            return;
        }

        const sid = props.firmRetainerSessionId;
        const province = props.firmRetainerProvince;
        if (!sid || !province) {
            setFirmSelectError('Session or province is not ready.');
            return;
        }

        setRetainerSourceMode('firm');
        setSelectedFirmFileName(fname);
        props.setFirmRetainerSourceName?.(null);
        props.setUploadedFile(null);
        props.setRetainerState({
            retainerRequired: true,
            defaultRetainerRequired: false,
            textBoxRetainerRequired: false,
            uploadRetainer: true,
            textBasedRetainer: null
        });

        setFirmDownloading(true);
        try {
            const buf = await downloadFirmRetainerFile(sid, province, fname);
            const file = new File([buf], fname, { type: 'application/pdf' });
            props.setUploadedFile(file);
            props.setFirmRetainerSourceName?.(fname);
        } catch (err) {
            setFirmSelectError((err as Error).message);
            setSelectedFirmFileName(null);
            setRetainerSourceMode('');
            props.setFirmRetainerSourceName?.(null);
            props.setRetainerState({
                retainerRequired: true,
                defaultRetainerRequired: false,
                textBoxRetainerRequired: false,
                uploadRetainer: false,
                textBasedRetainer: null
            });
        } finally {
            setFirmDownloading(false);
        }
    };

    const handlePdfFromComputer = (file: File) => {
        setFirmSelectError(null);
        const t = (file.type || '').toLowerCase();
        const name = file.name.toLowerCase();
        const isPdf =
            t === 'application/pdf' ||
            t === 'application/x-pdf' ||
            name.endsWith('.pdf');
        if (!isPdf) {
            setFirmSelectError(
                agreementMode
                    ? 'You can only upload agreements in PDF format.'
                    : 'You can only upload retainers in PDF format.'
            );
            return;
        }
        setRetainerSourceMode('upload');
        setSelectedFirmFileName(null);
        props.setFirmRetainerSourceName?.(null);
        props.setRetainerState({
            retainerRequired: true,
            defaultRetainerRequired: false,
            textBoxRetainerRequired: false,
            uploadRetainer: true,
            textBasedRetainer: null
        });
        props.setUploadedFile(file);
    };

    const handleTextBoxChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        props.setRetainerState({
            ...props.retainerState,
            textBasedRetainer: e.target.value
        });
    };

    const hasTextRetainer = props.retainerState.textBoxRetainerRequired;
    const hasUploadRetainer =
        props.retainerState.uploadRetainer && !props.retainerState.textBoxRetainerRequired;

    const showUploadDropzone =
        props.retainerState.retainerRequired && hasUploadRetainer && retainerSourceMode !== 'firm';

    const uploadZoneFileName = showUploadDropzone
        ? props.uploadedRetainerFile?.name ?? null
        : null;

    return (
        <>
            <h3>Add {noun}</h3>
            <Alert className="mb-3">
                {agreementMode
                    ? 'Optionally select, upload or write an agreement.'
                    : 'Optionally select, upload or write a retainer.'}
            </Alert>

            <Form.Check
                type="checkbox"
                checked={props.retainerState.retainerRequired}
                label={`Require ${noun}`}
                onChange={handleRequireRetainer}
            />

            {props.retainerState.retainerRequired ? (
                <Form.Group className="mt-3">
                    <Form.Label>{agreementMode ? 'Select Agreement' : 'Select Retainer'}</Form.Label>
                    <Form.Select
                        value={selectRetainerValue}
                        disabled={firmDownloading}
                        onChange={(ev) => { void handleSelectRetainerChange(ev); }}
                    >
                        <option value="">{agreementMode ? 'select agreement..' : 'select retainer..'}</option>
                        {firmNames.map((n) => (
                            <option key={n} value={firmOptionValue(n)}>
                                {n}
                            </option>
                        ))}
                        <option value={VAL_UPLOAD} style={{ fontWeight: 700 }}>
                            {agreementMode ? 'Upload New Agreement' : 'Upload New Retainer'}
                        </option>
                        <option value={VAL_WRITE} style={{ fontWeight: 700 }}>
                            {agreementMode ? 'Write New Agreement' : 'Write New Retainer'}
                        </option>
                    </Form.Select>
                    {firmDownloading ? (
                        <div className="mt-2">
                            <Spinner animation="border" size="sm" className="me-2" /> Loading…
                        </div>
                    ) : null}
                    {firmSelectError ? (
                        <Alert variant="danger" className="mt-2 py-2 mb-0">{firmSelectError}</Alert>
                    ) : null}
                </Form.Group>
            ) : null}

            {showUploadDropzone ? (
                <div className="mt-3">
                    <PdfDropzone
                        onFileSelect={handlePdfFromComputer}
                        uploadedFileName={uploadZoneFileName}
                        uploadButtonLabel="Upload"
                    />
                </div>
            ) : null}

            {props.retainerState.retainerRequired && hasTextRetainer ? (
                <Form.Group className="mt-3">
                    <Form.Label>{agreementMode ? 'Agreement text' : 'Retainer text'}</Form.Label>
                    <textarea
                        className="form-control"
                        rows={5}
                        placeholder={agreementMode ? 'Enter agreement text here…' : 'Enter retainer text here…'}
                        id="textBasedRetainer"
                        value={props.retainerState.textBasedRetainer ?? ''}
                        onChange={handleTextBoxChange}
                    />
                </Form.Group>
            ) : null}
        </>
    );
};

export default RetainerUploadComponent;
